import React from 'react'
import { Heart } from 'lucide-react'

const NARRATIVES = [
  {
    title: 'Quem somos',
    desc: 'Somos uma agência brasileira que organiza posicionamento, narrativa e conversão a partir de inteligência comportamental. Atuamos para que marcas comuniquem com mais clareza, sejam mais bem percebidas e transformem presença em resultado com mais consistência. Em resumo, somos a inquietude que inova e transforma conexões!',
  },
  {
    title: 'Por que existimos',
    desc: 'Existimos porque muitas marcas comunicam muito e significam pouco. A Dorac nasceu para corrigir essa distorção, organizando percepção, linguagem e presença de forma que a marca seja entendida, desejada e escolhida. Ou seja, existimos porque muitas marcas produzem comunicação em volume, mas não constroem significado, confiança nem diferenciação. A Dorac veio para corrigir isso, organizando percepção, linguagem, oferta e presença digital para que a marca seja entendida com mais rapidez, desejada com mais consistência e escolhida com mais segurança.',
  },
  {
    title: 'Como fazemos',
    desc: 'Começamos lendo o contexto da marca, a percepção atual do público, os ruídos da comunicação e as objeções de decisão. Depois organizamos posicionamento, narrativa, conteúdo, canais, criativos e pontos de conversão para que a marca fale com mais clareza, prove mais valor e gere avanço real na jornada do público.',
  },
  {
    title: 'Por que isso converte',
    desc: 'Converte porque torna a comunicação mais compreensível, reduz insegurança, fortalece a percepção de valor e facilita a decisão. Quando a marca explica melhor o que entrega, para quem serve, por que é diferente e por que merece confiança, ela vende com menos atrito e mais consistência.',
  },
]

const TIMELINE = [
  {
    year: '2016',
    title: 'O início da inquietude',
    desc: 'A Dorac nasce para colocar pessoas no centro da estratégia, transformando escuta, intenção e inteligência em conexões mais humanas e resultados consistentes.',
  },
  {
    year: '2018',
    title: 'Desenvolvimento do Método DORAC',
    desc: 'A partir de centenas de pesquisas, a Dra. Samantha Dorabiato sistematizou o Método DORAC. Testado e validado, ele nasceu para transformar comportamento humano em ação estratégica.',
  },
  {
    year: '2020',
    title: 'Aplicação da tese e consolidação do método',
    desc: 'Com bases na análise do comportamento, na fenomenologia e na percepção humana, o Método DORAC foi consolidado como uma metodologia estratégica centrada na compreensão das pessoas.',
  },
  {
    year: '2022',
    title: 'Revisão multidisciplinar do método',
    desc: 'Após pesquisas aplicadas e validações, a tese do Método DORAC foi revisada e aprovada por uma banca multidisciplinar e, em 5 de dezembro, a Agência Dorac nasceu oficialmente com a abertura de seu CNPJ.',
  },
  {
    year: '2024',
    title: 'Expansão e consolidação',
    desc: 'A Dorac expandiu sua equipe e seu portfólio, iniciou projetos com grandes marcas regionais e refinou sua tese. Começou a preparar seus processos internos para automação e crescimento em escala.',
  },
  {
    year: '2026',
    title: 'Inteligência Comportamental',
    desc: 'Incorporamos neurociência e ciência do comportamento ao nosso processo, elevando a precisão estratégica dos projetos.',
  },
  {
    year: '2028',
    title: 'Visão de futuro',
    desc: 'Ser a referência regional em marketing humanizado com inteligência comportamental aplicada, reconhecida por construir marcas mais fortes, memoráveis e comercialmente relevantes.',
    isVision: true,
  },
]

const VALUES = [
  {
    title: 'Intenção',
    desc: 'Toda comunicação precisa ter direção clara, coerência estratégica e impacto real no negócio.',
  },
  {
    title: 'Humanidade',
    desc: 'Pessoas não são números nem alvos. São decisões influenciadas por contexto, emoção, repertório e confiança.',
  },
  {
    title: 'Colaboração',
    desc: 'Resultados melhores surgem quando marca e agência constroem juntas, com troca real de visão, contexto e responsabilidade.',
  },
  {
    title: 'Transparência',
    desc: 'Confiança se constrói com verdade, clareza, alinhamento e responsabilidade na comunicação.',
  },
  {
    title: 'Excelência',
    desc: 'Buscamos resultado com qualidade estratégica, cuidado na forma e respeito ao contexto da marca.',
  },
  {
    title: 'Ética',
    desc: 'Crescimento consistente não exige manipulação. Exige leitura humana, inteligência e responsabilidade estratégica.',
  },
]

function AboutUs() {
  return (
    <>
      <section className='dorac-grid-bg pb-20 pt-32'>
        <div className='container mx-auto max-w-4xl px-4 text-center'>
          <h1 className='section-title mb-6'>A inquietude que inova e transforma conexões</h1>
          <p className='mb-4 text-base leading-relaxed text-dorac-ink/60 md:text-lg'>
            A maioria das marcas falam, poucas realmente marcam. Em um cenário saturado por estímulos, promessas
            rápidas e fórmulas vazias, não vence quem aparece mais. Vence quem consegue entrar na mente, tocar a
            emoção certa, construir confiança e se tornar impossível de ignorar. É nesse ponto que a Dorac existe.
          </p>
          <p className='text-base leading-relaxed text-dorac-ink/60 md:text-lg'>
            A Dorac não existe para produzir comunicação bonita e descartável. Existe para ajudar marcas a organizar
            o que dizem, como se posicionam, como provam valor e como transformam atenção em relacionamento e
            relacionamento em resultado. Para isso, analisamos percepção de marca, tensões do público, objeções
            invisíveis, proposta de valor, narrativa, canais e pontos de conversão. O objetivo é tornar a comunicação
            mais consistente, mais convincente e mais comercialmente eficaz.
          </p>
        </div>
      </section>

      <section className='bg-white py-24'>
        <div className='container mx-auto max-w-4xl px-4'>
          <div className='flex flex-col'>
            {NARRATIVES.map((n, i) => (
              <div
                key={n.title}
                className='grid grid-cols-1 gap-4 border-t border-dorac-border py-10 last:border-b sm:grid-cols-[6rem_1fr] sm:gap-10'
              >
                <div className='flex items-start gap-4 sm:block'>
                  <span
                    className='font-display text-5xl font-black leading-none text-dorac-purple/15 sm:text-6xl'
                  >
                    {String(i + 1).padStart(2, '0')}
                  </span>
                  <h3 className='text-xl font-bold text-dorac-purple sm:hidden'>{n.title}</h3>
                </div>
                <div className='max-w-xl'>
                  <h3 className='mb-2 hidden text-xl font-bold text-dorac-purple sm:block'>{n.title}</h3>
                  <p className='text-base leading-relaxed text-dorac-ink/70'>{n.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className='bg-white py-24'>
        <div className='container mx-auto px-4'>
          <div className='grid grid-cols-1 gap-6 md:grid-cols-2'>
            <div
              className='rounded-2xl p-8 text-white'
              style={{ background: '#491C47' }}
            >
              <span className='mb-2 block text-xs font-bold uppercase tracking-widest text-white/50' style={{ color: '#FFE98C' }}>Nossa missão</span>
              <h2 className='mb-3 text-2xl font-bold'>Mudança dos status quo</h2>
              <p className='text-sm leading-relaxed text-white/70'>
                Ser a inquietude que inova e transforma conexões.
              </p>
            </div>

            <div
              className='rounded-2xl p-8 text-white'
              style={{ background: '#491C47' }}
            >
              <span className='mb-2 block text-xs font-bold uppercase tracking-widest text-white/50' style={{ color: '#FFE98C' }}>Nossa visão</span>
              <h2 className='mb-3 text-2xl font-bold'>Referência até 2028</h2>
              <p className='text-sm leading-relaxed text-white/70'>
                Até 2028 ser a referência em marketing humanizado com inteligência comportamental aplicada na região
                central do Paraná.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className='dorac-grid-bg py-24'>
        <div className='container mx-auto px-4'>
          <div className='mb-16 text-center'>
            <span className='mb-3 block text-xs font-bold uppercase tracking-widest text-dorac-purple'>
              O que nos guia
            </span>
            <h2 className='section-title mb-4'>Nossos Valores</h2>
            <p className='section-subtitle mx-auto'>
              Os princípios que orientam cada decisão, cada estratégia e cada entrega da Dorac.
            </p>
          </div>

          <div className='grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3'>
            {VALUES.map((value) => (
              <div key={value.title} className='card-dorac'>
                <h3 className='mb-2 text-lg font-bold text-dorac-purple'>{value.title}</h3>
                <p className='text-sm leading-relaxed text-dorac-ink/60'>{value.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className='dorac-grid-bg-dark py-24 text-white'>
        <div className='container mx-auto max-w-3xl px-4 text-center'>
          <span className='mb-3 block text-xs font-bold uppercase tracking-widest' style={{ color: '#FFE98C' }}>
            Manifesto da marca
          </span>
          <div className='space-y-4 text-base leading-relaxed text-white/80 md:text-lg'>
            <p>Não estamos aqui para encher o mercado de conteúdo.</p>
            <p className='font-bold text-white'>Estamos aqui para devolver intenção à comunicação.</p>
            <p>
              Idealizamos que marcas não crescem de verdade quando apenas aparecem. Crescem quando passam a fazer
              sentido.
            </p>
            <p>Acreditamos que comportamento humano não é detalhe de estratégia. É o centro dela.</p>
            <p>
              Porque toda decisão de marca acontece antes dentro das pessoas, e só depois no caixa, no clique, no
              contrato ou na recorrência.
            </p>
            <p>
              Por isso, a Dorac existe para transformar leitura humana em posicionamento forte, narrativa coerente e
              conversão consistente.
            </p>
          </div>
          <div className='mt-10 flex items-center justify-center gap-2'>
            <Heart className='h-5 w-5' style={{ color: '#FFE98C' }} />
          </div>
          <p className='mt-4 font-display text-xl font-black md:text-2xl'>
            Somos a Dorac.
            <br />
            Humanos na essência. Estratégicos na forma. Inconfundíveis na intenção.
          </p>
        </div>
      </section>

      <section className='bg-white py-24'>
        <div className='container mx-auto px-4'>
          <div className='mb-16 text-center'>
            <span className='mb-3 block text-xs font-bold uppercase tracking-widest text-dorac-purple'>
              Nossa história
            </span>
            <h2 className='section-title'>A jornada da Dorac</h2>
          </div>

          <div className='relative mx-auto max-w-3xl'>
            <div className='absolute bottom-0 left-6 top-0 w-px bg-dorac-border' />
            <div className='flex flex-col gap-10'>
              {TIMELINE.map((item) => (
                <div key={item.year} className='relative pl-16'>
                  <div
                    className='absolute left-0 top-0 flex h-12 w-12 items-center justify-center rounded-full text-sm font-bold text-white'
                    style={{ background: '#491C47' }}
                  >
                    {item.year.slice(2)}
                  </div>
                  <div className='mb-1 text-xs font-bold text-dorac-purple'>
                    {item.year} {item.isVision && '(VISÃO)'}
                  </div>
                  <h3 className='mb-1 text-lg font-bold text-dorac-ink'>{item.title}</h3>
                  <p className='text-sm leading-relaxed text-dorac-ink/60'>{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className='dorac-grid-bg py-24'>
        <div className='container mx-auto max-w-2xl px-4 text-center'>
          <h2 className='section-title mb-6'>Vamos construir algo inconfundível juntos?</h2>
          <p className='section-subtitle mx-auto mb-8'>
            Cada projeto na Dorac nasce de uma pergunta decisiva: o que precisa acontecer dentro das pessoas para que
            essa marca cresça por fora?
          </p>
          <a
            href='https://api.whatsapp.com/send?phone=554396659096&text=Ol%C3%A1%2C%20vim%20pelo%20site%20e%20gostaria%20de%20iniciar%20uma%20conversa%20com%20a%20Dorac!'
            target='_blank'
            rel='noopener noreferrer'
            className='btn-dorac px-8 py-4 hover:!bg-[#25D366]'
          >
            Iniciar conversa
          </a>
        </div>
      </section>
    </>
  )
}

export default AboutUs
